/**
 * NewDossiersTab — Lists dossiers created via the webapp (ID >= 90000)
 * Admin-only tab in the sidebar.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/core/api/client';
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';

interface Dossier {
  id: number;
  status: string;
  type: string;
  sub_type: string | null;
  property_street: string | null;
  property_number: string | null;
  property_postcode: string | null;
  property_city: string | null;
  property_type: string | null;
  date_demande: string | null;
  created_at: string;
  flag_active: boolean;
}

export const NewDossiersTab: React.FC = () => {
  const navigate = useNavigate();
  const [dossiers, setDossiers] = useState<Dossier[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetch = async () => {
      try {
        const res: any = await apiClient.get('/api/dossiers/list');
        if (res.success) setDossiers(res.data);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const filtered = dossiers.filter(d => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      String(d.id).includes(q) ||
      d.property_street?.toLowerCase().includes(q) ||
      d.property_city?.toLowerCase().includes(q) ||
      d.status?.toLowerCase().includes(q) ||
      d.type?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="new-dossiers-tab">
      <div className="new-dossiers-header">
        <h2>Dossiers webapp</h2>
        <button onClick={() => navigate('/admin/nouveau-dossier')} className="new-dossiers-create-btn">
          <PlusIcon className="w-4 h-4" />
          Nouveau dossier
        </button>
      </div>

      <div className="new-dossiers-search">
        <MagnifyingGlassIcon className="w-4 h-4" />
        <input
          type="text"
          placeholder="Rechercher par ID, adresse, ville, statut..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="new-dossiers-loading">Chargement...</div>
      ) : filtered.length === 0 ? (
        <div className="new-dossiers-empty">
          {dossiers.length === 0
            ? 'Aucun dossier créé via la webapp'
            : `Aucun résultat pour "${search}"`
          }
        </div>
      ) : (
        <div className="new-dossiers-list">
          {filtered.map(d => (
            <div
              key={d.id}
              className="new-dossiers-card"
              onClick={() => navigate(`/admin/affaire/${d.id}`)}
            >
              <div className="new-dossiers-card-left">
                <span className="new-dossiers-card-id">#{d.id}</span>
                <span className={`new-dossiers-card-status ${d.flag_active ? '' : 'inactive'}`}>
                  {d.status}
                </span>
              </div>
              <div className="new-dossiers-card-center">
                <span className="new-dossiers-card-address">
                  {[d.property_number, d.property_street].filter(Boolean).join(' ') || 'Adresse non renseignée'}
                </span>
                <span className="new-dossiers-card-city">
                  {[d.property_postcode, d.property_city].filter(Boolean).join(' ')}
                </span>
              </div>
              <div className="new-dossiers-card-right">
                <span className="new-dossiers-card-type">{d.type}</span>
                <span className="new-dossiers-card-date">
                  {d.date_demande ? new Date(d.date_demande).toLocaleDateString('fr-BE') : '-'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
